import React, { useRef } from 'react'
import { Form, Input, Typography, Space, Tag, Button, Tooltip } from 'antd'
import { Building2, User, Phone, Mail, ExternalLink, Link2, NotebookTabs } from 'lucide-react'
import { Supplier } from '../../types/ffe'
import { useFFEStore } from '../../store/ffeStore'
import { useApi } from '../../services/ApiContext'
import debounce from 'debounce'
import { Link } from 'react-router-dom'

const { Text } = Typography
const { TextArea } = Input

interface Props {
  supplier: Supplier
}

export const SupplierCard: React.FC<Props> = ({ supplier }) => {
  const updateSupplier = useFFEStore((state) => state.updateSupplier)
  const api = useApi()

  // debounce from onchange to api
  const updateSupplierInApi = async (updates: Partial<Supplier>) => {
    await api.updateSupplier(supplier._id, { ...supplier, ...updates })
  }

  const debouncedUpdateSupplierInApi = useRef(
    debounce(updateSupplierInApi, 1500)
  )

  const onChange = async (updates: Partial<Supplier>) => {
    updateSupplier({ ...supplier, ...updates })
    debouncedUpdateSupplierInApi.current(updates)
  }
  const handleContactChange = (
    field: keyof NonNullable<Supplier['contact']>,
    value: string
  ) => {
    onChange({
      contact: {
        ...supplier.contact,
        [field]: value
      }
    })
  }

  return (
    // <Card size="small" className="w-[300px]">
    <Form layout="vertical" className="space-y-2 w-[300px]">
      <div className="flex items-center justify-between mb-2">
        <Text className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-400" /> {supplier.name}
        </Text>
        {/* Visit supplier page */}
        <Tooltip title="Open in supplier list">
          <Link target="_blank" to={`/app/suppliers/${supplier._id}`}>
            <Button
              type="text"
              icon={<NotebookTabs className="text-gray-400 w-4 h-4" />}
            ></Button>
          </Link>
        </Tooltip>
      </div>

      {supplier.isArchived && <Tag color="red">Archived</Tag>}

      <Text type="secondary" className="text-xs block mt-4 mb-2">
        Contact Details
      </Text>

      <Form.Item>
        <Input
          value={supplier.contact?.name}
          onChange={(e) => handleContactChange('name', e.target.value)}
          placeholder="Contact name"
          prefix={<User className="h-4 w-4 text-gray-400" />}
        />
      </Form.Item>

      <Space.Compact className="w-full">
        <Input
          value={supplier.contact?.phone}
          onChange={(e) => handleContactChange('phone', e.target.value)}
          placeholder="Phone"
          prefix={<Phone className="h-4 w-4 text-gray-400" />}
        />
        <Input
          value={supplier.contact?.email}
          onChange={(e) => handleContactChange('email', e.target.value)}
          placeholder="Email"
          prefix={<Mail className="h-4 w-4 text-gray-400" />}
        />
      </Space.Compact>

      <Text type="secondary" className="text-xs block mt-4 mb-2">
        Additional Information
      </Text>

      <Form.Item>
        <TextArea
          value={supplier.address}
          onChange={(e) => onChange({ address: e.target.value })}
          placeholder="Address"
          rows={2}
        />
      </Form.Item>

      <Form.Item>
        <Space.Compact className="w-full">
          {supplier.website && (
            <Tooltip title="Open supplier website">
              <Button
                icon={<ExternalLink className="h-4 w-4 text-gray-400" />}
                onClick={() => window.open(supplier.website, '_blank')}
              />
            </Tooltip>
          )}
          <Input
            value={supplier.website}
            onChange={(e) => onChange({ website: e.target.value })}
            placeholder="Website"
            prefix={
              !supplier.website && <Link2 className="h-4 w-4 text-gray-400" />
            }
          />
        </Space.Compact>
      </Form.Item>

      <Form.Item>
        <TextArea
          value={supplier.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Notes"
          rows={2}
        />
      </Form.Item>
    </Form>
    // </Card>
  )
}
